import { useCallback, useEffect, useRef, useState } from "react";
import { useBackend } from "../../backend/BackendProvider";
import type { Attachment, EntityId } from "../../domain/entities";
import { ATTACHMENT_MAX_PER_SELECTION, validateAttachmentFile } from "../../domain/attachments";

export type DraftAttachmentStatus = "validating" | "waiting" | "uploading" | "uploaded" | "cancelled" | "failed";

export interface DraftAttachment {
  key: string;
  file: File;
  status: DraftAttachmentStatus;
  progress: number;
  error: string | null;
  attachmentId: EntityId | null;
  controller: AbortController | null;
}

export function useAttachmentUploader(taskId: string, source: "task" | "comment", onUploaded?: (attachment: Attachment) => void) {
  const backend = useBackend();
  const [files, setFiles] = useState<DraftAttachment[]>([]);
  const filesRef = useRef<DraftAttachment[]>(files);
  const uploadedRef = useRef(onUploaded);
  useEffect(() => { filesRef.current = files; }, [files]);
  useEffect(() => { uploadedRef.current = onUploaded; }, [onUploaded]);

  const patch = useCallback((key: string, value: Partial<DraftAttachment>) => {
    setFiles((current) => current.map((item) => item.key === key ? { ...item, ...value } : item));
  }, []);

  const run = useCallback(async (key: string, file: File) => {
    const controller = new AbortController();
    patch(key, { status: "validating", progress: 0, error: null, controller, attachmentId: null });
    try {
      const invalid = validateAttachmentFile(file);
      if (invalid) throw new Error(invalid);
      const reservation = await backend.attachments.reserve(taskId, { name: file.name, mediaType: file.type, byteSize: file.size, source }, { clientMutationId: key });
      if (controller.signal.aborted) { await backend.attachments.cancel(reservation.id, { clientMutationId: crypto.randomUUID() }).catch(() => undefined); patch(key, { status: "cancelled", error: "Upload cancelled.", controller: null }); return; }
      patch(key, { status: "waiting", attachmentId: reservation.id });
      const uploaded = ["ready", "uploaded"].includes(reservation.state) ? reservation : await backend.attachments.upload(reservation.id, file, {
        signal: controller.signal,
        onProgress: (progress) => patch(key, { status: "uploading", progress }),
      });
      const finalized = await backend.attachments.finalize(uploaded.id, { clientMutationId: crypto.randomUUID(), expectedVersion: uploaded.version });
      patch(key, { status: "uploaded", progress: 1, attachmentId: finalized.id });
      uploadedRef.current?.(finalized);
    } catch (error) {
      if (controller.signal.aborted) { patch(key, { status: "cancelled", error: "Upload cancelled.", controller: null }); return; }
      patch(key, { status: "failed", error: error instanceof Error ? error.message : "The file could not be uploaded.", controller: null });
    }
  }, [backend, patch, source, taskId]);

  const addFiles = useCallback((selected: File[]) => {
    if (!selected.length) return;
    const rows: DraftAttachment[] = selected.map((file, index) => {
      const invalid = index >= ATTACHMENT_MAX_PER_SELECTION ? `Select at most ${ATTACHMENT_MAX_PER_SELECTION} files at a time.` : validateAttachmentFile(file);
      return { key: crypto.randomUUID(), file, status: invalid ? "failed" : "waiting", progress: 0, error: invalid, attachmentId: null, controller: null };
    });
    setFiles((current) => [...current, ...rows]);
    for (const row of rows) if (!row.error) void run(row.key, row.file);
  }, [run]);

  const cancel = useCallback(async (key: string) => {
    const item = filesRef.current.find((entry) => entry.key === key);
    if (!item) return;
    item.controller?.abort();
    patch(key, { status: "cancelled", error: "Upload cancelled.", controller: null });
    if (item.attachmentId) {
      try { await backend.attachments.cancel(item.attachmentId, { clientMutationId: crypto.randomUUID() }); } catch { /* The server may already have released it. */ }
    }
  }, [backend, patch]);

  const retry = useCallback((key: string) => {
    const item = filesRef.current.find((entry) => entry.key === key);
    if (item) void run(item.key, item.file);
  }, [run]);

  const remove = useCallback((key: string) => {
    const item = filesRef.current.find((entry) => entry.key === key);
    if (item && item.status !== "uploaded") void cancel(key);
    setFiles((current) => current.filter((entry) => entry.key !== key));
  }, [cancel]);

  const reset = useCallback(() => {
    for (const item of filesRef.current) {
      item.controller?.abort();
      if (item.attachmentId && item.status !== "uploaded") void backend.attachments.cancel(item.attachmentId, { clientMutationId: crypto.randomUUID() }).catch(() => undefined);
    }
    setFiles([]);
  }, [backend]);

  return {
    files,
    addFiles,
    cancel,
    retry,
    remove,
    reset,
    uploading: files.some((item) => item.status === "validating" || item.status === "waiting" || item.status === "uploading"),
    unresolved: files.some((item) => item.status !== "uploaded"),
    completedIds: files.filter((item) => item.status === "uploaded" && item.attachmentId).map((item) => item.attachmentId!),
  };
}
